<script>
  import { onMount, onDestroy } from "svelte";
  import MarkerMaterial from "./MarkerMaterial.svelte";
  import MarkerTruck from "./MarkerTruck.svelte";
  import { fetchJobsLive, activeJobs } from "../../data";
  import { selectedJobs, jobsListStore, liveJobsStore } from "../../stores";

  let jobsData = activeJobs && activeJobs.jobs ? activeJobs : null;
  let pollTimer = null;
  let isFetching = false;
  let currentAbortController = null;
  let isDestroyed = false;

  $: currentJobs = (jobsData && jobsData.jobs && jobsData.jobs.length > 0)
    ? jobsData.jobs
    : (($jobsListStore && $jobsListStore.length > 0)
      ? $jobsListStore
      : ((activeJobs && activeJobs.jobs) ? activeJobs.jobs : []));

  // Polling interval between completed requests (4 seconds)
  const POLL_INTERVAL_MS = 2500;

  async function pollJobs() {
    if (isDestroyed) return;

    // 1. FLUSH / ABORT any previous in-flight request before launching a new one
    if (currentAbortController) {
      try {
        currentAbortController.abort();
      } catch (e) {}
      currentAbortController = null;
    }

    // 2. Do not poll if the browser tab/window is hidden or minimized
    if (typeof document !== "undefined" && document.hidden) {
      return;
    }

    currentAbortController = new AbortController();
    const signal = currentAbortController.signal;

    isFetching = true;
    try {
      const res = await fetchJobsLive({
        signal,
        updateStore: false, // Prevents thrashing and re-rendering all JobCards in the DOM
      });
      if (res && res.jobs && !signal.aborted && !isDestroyed) {
        jobsData = res;
        liveJobsStore.set(res.jobs);
      }
    } catch (err) {
      if (err && err.name !== "AbortError") {
        console.error("Error polling truck locations:", err);
      }
    } finally {
      isFetching = false;
      // 3. Schedule next poll using trailing setTimeout so requests NEVER overlap or pile up
      if (!isDestroyed && typeof document !== "undefined" && !document.hidden) {
        if (pollTimer) clearTimeout(pollTimer);
        pollTimer = setTimeout(pollJobs, POLL_INTERVAL_MS);
      }
    }
  }

  export function filterjobs() {
    pollJobs();
  }

  function handleVisibilityChange() {
    if (document.hidden) {
      // Tab is hidden: flush active in-flight request and cancel timer to free resources
      if (currentAbortController) {
        currentAbortController.abort();
        currentAbortController = null;
      }
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
    } else {
      // Tab is visible again: trigger immediate fresh poll and resume schedule
      pollJobs();
    }
  }

  onMount(() => {
    isDestroyed = false;
    pollJobs();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
  });

  function getTruckRego(t) {
    if (!t) return "";
    if (typeof t.truck === "string" && t.truck.trim()) return t.truck.trim();
    if (t.truck && typeof t.truck === "object" && t.truck.id) return String(t.truck.id).trim();
    if (t.rego) return String(t.rego).trim();
    if (t.truck_rego) return String(t.truck_rego).trim();
    if (t.id && typeof t.id === "string") {
      const parts = t.id.split("-");
      if (parts.length > 1 && parts[parts.length - 1].length <= 8) {
        return parts[parts.length - 1];
      }
      return t.id;
    }
    return "TRUCK";
  }

  function getTruckLoadsCount(t) {
    if (!t) return 0;
    if (typeof t.loads_count === "number") return t.loads_count;
    if (typeof t.load_count === "number") return t.load_count;
    if (typeof t.loads === "number") return t.loads;
    if (Array.isArray(t.loads)) {
      return t.loads.length;
    }
    return 0;
  }

  function isJobSelected(job, selectedList) {
    if (!job) return false;
    const isJobActive = (job.status || "active").toLowerCase() === "active";
    if (!isJobActive) return false;
    if (!selectedList || selectedList.length === 0) return true;
    const candidates = [job.id, job._id, job.job_number, job.__job_number__]
      .filter((v) => v != null)
      .map(String);
    return selectedList.some((sel) => candidates.includes(String(sel)));
  }

  onDestroy(() => {
    isDestroyed = true;
    if (currentAbortController) {
      currentAbortController.abort();
      currentAbortController = null;
    }
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    }
  });
</script>

{#if currentJobs && currentJobs.length > 0}
  {#each currentJobs as job, jobIdx (job.id || job._id || jobIdx)}
    {#if isJobSelected(job, $selectedJobs)}
      {#each job.materials || [] as material, matIdx (material.id || material._id || matIdx)}
        {#if material.load_address && material.load_address.coordinates && material.load_address.coordinates.lat != null && material.load_address.coordinates.lng != null}
          <MarkerMaterial
            lat={material.load_address.coordinates.lat}
            lon={material.load_address.coordinates.lng}
            label={material.name}
            type="load"
            id={material.id}
          />
        {/if}
        {#if material.disposal_address && material.disposal_address.coordinates && material.disposal_address.coordinates.lat != null && material.disposal_address.coordinates.lng != null}
          <MarkerMaterial
            lat={material.disposal_address.coordinates.lat}
            lon={material.disposal_address.coordinates.lng}
            label={material.name}
            type="dispose"
            id={material.id}
          />
        {/if}
        {#if material.allocated_trucks}
          {#each material.allocated_trucks || [] as truck, truckIdx (truck._id || truck.id || material.id + "_" + (truck.truck ? truck.truck.id || truck.truck : "truck") + "_day" + (truck.scheduled_day || 1) + "_" + truckIdx)}
            {#if truck.current_coords && truck.current_coords.lat != null && truck.current_coords.lng != null}
              <MarkerTruck
                lat={truck.current_coords.lat}
                lon={truck.current_coords.lng}
                rego={getTruckRego(truck)}
                loadsCount={getTruckLoadsCount(truck)}
                label={getTruckRego(truck)}
                id={truck.truck ? (truck.truck.id || truck.truck) : truck.id}
                direction={truck.direction}
                loads={truck.loads || []}
                driver={truck.driver || truck.truck?.default_driver}
                trailer={truck.trailer || truck.truck?.default_trailer}
                destination={truck.destination}
                capacity={truck.total_capacity || truck.truck?.capacity}
                materialName={material.name || ""}
                customerName={job.customer?.name || (typeof job.customer === 'string' ? job.customer : '')}
                jobNumber={job.__job_number__ || job.job_number || job.id || ""}
              />
            {/if}
          {/each}
        {/if}
      {/each}
    {/if}
  {/each}
{/if}
